import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { FileItem, isDirectory } from "./FileGrid";
import { ensureFolder, moveItems, MoveOutcome } from "./app/move";

function MoveDialog({
  open,
  keys,
  files,
  onClose,
  onDone,
}: {
  open: boolean;
  keys: string[];
  /** Current folder contents, used to suggest existing destinations. */
  files: FileItem[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [destination, setDestination] = useState("");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0, current: "" });
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<MoveOutcome[] | null>(null);

  useEffect(() => {
    if (!open) return;
    setDestination("");
    setBusy(false);
    setError(null);
    setResults(null);
    setProgress({ done: 0, total: 0, current: "" });
  }, [open]);

  const folderOptions = useMemo(
    () =>
      files
        .filter(isDirectory)
        .map((f) => f.key)
        .filter((k) => !keys.includes(k)),
    [files, keys]
  );

  const failed = results?.filter((r) => !r.ok) ?? [];

  async function handleMove() {
    const dest = destination.trim().replace(/^\/+|\/+$/g, "");
    if (!dest) {
      setError("Enter a destination folder name");
      return;
    }
    if (keys.some((k) => dest === k || dest.startsWith(`${k}/`))) {
      setError("Cannot move a folder into itself");
      return;
    }

    setBusy(true);
    setError(null);
    setProgress({ done: 0, total: keys.length, current: "" });

    try {
      await ensureFolder(dest);
      const outcome = await moveItems(keys, dest, (done, total, current) =>
        setProgress({ done, total, current })
      );
      setResults(outcome);
      if (outcome.every((r) => r.ok)) {
        onDone();
        onClose();
      } else {
        onDone();
      }
    } catch (e: any) {
      setError(e?.message ?? "Move failed");
    } finally {
      setBusy(false);
    }
  }

  const percent =
    progress.total > 0 ? (progress.done / progress.total) * 100 : 0;

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ pb: 1 }}>
        Move {keys.length} item{keys.length === 1 ? "" : "s"}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          {error && (
            <Alert severity="error" sx={{ py: 0.5 }}>
              {error}
            </Alert>
          )}

          {results && failed.length > 0 && (
            <Alert severity="warning" sx={{ py: 0.5 }}>
              {failed.length} of {results.length} could not be moved:{" "}
              {failed
                .slice(0, 3)
                .map((f) => f.key.split("/").pop())
                .join(", ")}
              {failed.length > 3 && "…"}
            </Alert>
          )}

          {!busy && (
            <Autocomplete
              freeSolo
              options={folderOptions}
              value={destination}
              onChange={(_, v) => setDestination(v ?? "")}
              onInputChange={(_, v) => setDestination(v)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Destination folder"
                  placeholder="e.g. Wedding photos"
                  size="small"
                  autoFocus
                  helperText="Pick an existing folder or type a new name"
                />
              )}
            />
          )}

          {busy && (
            <Stack spacing={1}>
              <Typography variant="body2" noWrap>
                Moving {progress.current || "…"}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={percent}
                sx={{ height: 6, borderRadius: 3 }}
              />
              <Typography variant="caption" color="text.secondary">
                {progress.done} / {progress.total} · files are copied then
                removed, so this can take a while for large ones
              </Typography>
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {results ? "Close" : "Cancel"}
        </Button>
        <Button variant="contained" onClick={handleMove} disabled={busy}>
          {busy ? "Moving…" : "Move"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default MoveDialog;
